import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Palette, 
  ToggleLeft, 
  Type, 
  Store,
  Save,
  RotateCcw,
  Check,
  Loader2,
  Eye,
  Building2,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const WhiteLabelAdmin = () => {
  const { api } = useAuth();
  const [config, setConfig] = useState(null);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenantId, setTenantId] = useState('default');
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    loadConfig();
    loadPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadConfig = async () => {
    try {
      const response = await api.get(`/whitelabel/config?tenant_id=${tenantId}`);
      setConfig(response.data.config);
    } catch (error) {
      console.error('Failed to load config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadPresets = async () => {
    try {
      const response = await api.get('/whitelabel/presets');
      setPresets(response.data.presets);
    } catch (error) {
      console.error('Failed to load presets:', error);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/whitelabel/config', {
        tenant_id: tenantId,
        ...config
      });
      toast.success('Configuration saved!');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = async (presetId) => {
    try {
      const response = await api.post(`/whitelabel/apply-preset?tenant_id=${tenantId}&preset_id=${presetId}`);
      setConfig(response.data.config);
      toast.success(`Applied ${presetId} preset`);
    } catch (error) {
      toast.error('Failed to apply preset');
    }
  };

  const resetConfig = async () => {
    try {
      const response = await api.delete(`/whitelabel/config/${tenantId}`);
      setConfig(response.data.config);
      toast.success('Reset to defaults');
    } catch (error) {
      toast.error('Failed to reset configuration');
    }
  };

  const updateBrand = (key, value) => {
    setConfig(prev => ({
      ...prev,
      brand: { ...prev.brand, [key]: value }
    }));
  };

  const updateColor = (key, value) => {
    setConfig(prev => ({
      ...prev,
      brand: {
        ...prev.brand,
        colors: { ...prev.brand?.colors, [key]: value }
      }
    }));
  };

  const updateFeature = (key, value) => {
    setConfig(prev => ({
      ...prev,
      features: { ...prev.features, [key]: value }
    }));
  };

  const updateCopy = (key, value) => {
    setConfig(prev => ({
      ...prev,
      copy: { ...prev.copy, [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 bg-muted/30" data-testid="whitelabel-admin-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold font-['Playfair_Display']">White-Label Controls</h1>
            <p className="text-muted-foreground mt-1">Configure branding for enterprise partners</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={resetConfig} data-testid="reset-config-btn">
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button onClick={saveConfig} disabled={saving} data-testid="save-config-btn">
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Tenant Selector */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Label className="font-medium">Tenant ID:</Label>
              <Input 
                value={tenantId} 
                onChange={(e) => setTenantId(e.target.value)}
                className="max-w-xs"
                placeholder="default"
                data-testid="tenant-id-input"
              />
              <Button variant="outline" size="sm" onClick={loadConfig}>
                Load
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Partner Presets */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Partner Presets
            </CardTitle>
            <CardDescription>Quick-apply branding for known partners</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {presets.map((preset) => (
                <Button
                  key={preset.preset_id}
                  variant="outline"
                  onClick={() => applyPreset(preset.preset_id)}
                  className="flex items-center gap-2"
                  data-testid={`preset-${preset.preset_id}`}
                >
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: `hsl(${preset.primary_color})` }}
                  />
                  {preset.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Main Configuration Tabs */}
        <Tabs defaultValue="brand" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="brand" className="flex items-center gap-2">
              <Palette className="h-4 w-4" />
              Brand
            </TabsTrigger>
            <TabsTrigger value="features" className="flex items-center gap-2">
              <ToggleLeft className="h-4 w-4" />
              Features
            </TabsTrigger>
            <TabsTrigger value="copy" className="flex items-center gap-2">
              <Type className="h-4 w-4" />
              Copy
            </TabsTrigger>
            <TabsTrigger value="stores" className="flex items-center gap-2">
              <Store className="h-4 w-4" />
              Stores
            </TabsTrigger>
          </TabsList>

          {/* Brand Tab */}
          <TabsContent value="brand">
            <Card>
              <CardHeader>
                <CardTitle>Brand Configuration</CardTitle>
                <CardDescription>Customize colors, logo, and visual identity</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Brand Name</Label>
                    <Input 
                      value={config?.brand?.name || ''} 
                      onChange={(e) => updateBrand('name', e.target.value)}
                      placeholder="Carted"
                      data-testid="brand-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tagline</Label>
                    <Input 
                      value={config?.brand?.tagline || ''} 
                      onChange={(e) => updateBrand('tagline', e.target.value)}
                      placeholder="Plan Once. Cart Done."
                      data-testid="brand-tagline-input"
                    />
                  </div>
                </div>

                {/* Colors */}
                <div className="space-y-4">
                  <Label className="text-base font-semibold">Theme Colors (HSL format)</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Primary</Label>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-8 h-8 rounded border"
                          style={{ backgroundColor: `hsl(${config?.brand?.colors?.primary || '125 16% 36%'})` }}
                        />
                        <Input 
                          value={config?.brand?.colors?.primary || ''} 
                          onChange={(e) => updateColor('primary', e.target.value)}
                          placeholder="125 16% 36%"
                          className="font-mono text-sm"
                          data-testid="color-primary-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Secondary</Label>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-8 h-8 rounded border"
                          style={{ backgroundColor: `hsl(${config?.brand?.colors?.secondary || '30 26% 93%'})` }}
                        />
                        <Input 
                          value={config?.brand?.colors?.secondary || ''} 
                          onChange={(e) => updateColor('secondary', e.target.value)}
                          placeholder="30 26% 93%"
                          className="font-mono text-sm"
                          data-testid="color-secondary-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Accent</Label>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-8 h-8 rounded border"
                          style={{ backgroundColor: `hsl(${config?.brand?.colors?.accent || '25 60% 75%'})` }}
                        />
                        <Input 
                          value={config?.brand?.colors?.accent || ''} 
                          onChange={(e) => updateColor('accent', e.target.value)}
                          placeholder="25 60% 75%"
                          className="font-mono text-sm"
                          data-testid="color-accent-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Logo URL */}
                <div className="space-y-2">
                  <Label>Logo URL (optional)</Label>
                  <Input 
                    value={config?.brand?.logo_url || ''} 
                    onChange={(e) => updateBrand('logo_url', e.target.value)}
                    placeholder="https://..."
                    data-testid="logo-url-input"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Features Tab */}
          <TabsContent value="features">
            <Card>
              <CardHeader>
                <CardTitle>Feature Toggles</CardTitle>
                <CardDescription>Enable or disable app features for this tenant</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { key: 'recipes', label: 'Recipe Discovery', desc: 'Search and browse recipes' },
                    { key: 'meal_planner', label: 'Meal Planner', desc: 'Weekly meal planning' },
                    { key: 'grocery_lists', label: 'Grocery Lists', desc: 'Generate shopping lists' },
                    { key: 'pantry', label: 'Pantry Tracking', desc: 'Track pantry inventory' },
                    { key: 'favorites', label: 'Favorites', desc: 'Save favorite recipes' },
                    { key: 'paths', label: 'Meal Paths', desc: 'Pre-built meal plan templates' },
                    { key: 'ai_concierge', label: 'AI Concierge', desc: 'AI meal suggestions' },
                    { key: 'store_export', label: 'Store Export', desc: 'Export to grocery stores' },
                    { key: 'household', label: 'Household', desc: 'Collaborative features' },
                    { key: 'budget_tracking', label: 'Budget Tracking', desc: 'Track grocery spending' },
                    { key: 'subscription', label: 'Subscription Upsells', desc: 'Show Pro/Plus upgrades' },
                  ].map(({ key, label, desc }) => (
                    <div 
                      key={key} 
                      className="flex items-center justify-between p-4 border rounded-lg"
                      data-testid={`feature-${key}`}
                    >
                      <div>
                        <p className="font-medium">{label}</p>
                        <p className="text-sm text-muted-foreground">{desc}</p>
                      </div>
                      <Switch
                        checked={config?.features?.[key] !== false}
                        onCheckedChange={(checked) => updateFeature(key, checked)}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Copy Tab */}
          <TabsContent value="copy">
            <Card>
              <CardHeader>
                <CardTitle>Copy Overrides</CardTitle>
                <CardDescription>Customize terminology and messaging</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Navigation Labels */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">Navigation Labels</Label>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { key: 'nav_recipes', label: 'Recipes Nav', placeholder: 'Find Recipes' },
                      { key: 'nav_meal_planner', label: 'Planner Nav', placeholder: 'Meal Planner' },
                      { key: 'nav_grocery_lists', label: 'Lists Nav', placeholder: 'Grocery Lists' },
                    ].map(({ key, label, placeholder }) => (
                      <div key={key} className="space-y-2">
                        <Label className="text-sm text-muted-foreground">{label}</Label>
                        <Input 
                          value={config?.copy?.[key] || ''} 
                          onChange={(e) => updateCopy(key, e.target.value)}
                          placeholder={placeholder}
                          data-testid={`copy-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Core Terms */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">Core Terminology</Label>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { key: 'meal', label: 'Meal (singular)', placeholder: 'Meal' },
                      { key: 'meals', label: 'Meals (plural)', placeholder: 'Meals' },
                      { key: 'grocery_list', label: 'Grocery List', placeholder: 'Grocery List' },
                      { key: 'shopping_list', label: 'Shopping List', placeholder: 'Shopping List' },
                      { key: 'meal_plan', label: 'Meal Plan', placeholder: 'Meal Plan' },
                      { key: 'pantry', label: 'Pantry', placeholder: 'Pantry' },
                    ].map(({ key, label, placeholder }) => (
                      <div key={key} className="space-y-2">
                        <Label className="text-sm text-muted-foreground">{label}</Label>
                        <Input 
                          value={config?.copy?.[key] || ''} 
                          onChange={(e) => updateCopy(key, e.target.value)}
                          placeholder={placeholder}
                          data-testid={`copy-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">Action Labels</Label>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { key: 'export_to_store', label: 'Export Button', placeholder: 'Send to Store' },
                      { key: 'welcome_message', label: 'Welcome Message', placeholder: 'Welcome to Carted!' },
                    ].map(({ key, label, placeholder }) => (
                      <div key={key} className="space-y-2">
                        <Label className="text-sm text-muted-foreground">{label}</Label>
                        <Input 
                          value={config?.copy?.[key] || ''} 
                          onChange={(e) => updateCopy(key, e.target.value)}
                          placeholder={placeholder}
                          data-testid={`copy-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stores Tab */}
          <TabsContent value="stores">
            <Card>
              <CardHeader>
                <CardTitle>Store Configuration</CardTitle>
                <CardDescription>Configure which stores are available for export</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <Label className="text-base font-semibold">Enabled Stores</Label>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { id: 'instacart', name: 'Instacart', color: '#43B02A' },
                      { id: 'amazon', name: 'Amazon Fresh', color: '#FF9900' },
                      { id: 'walmart', name: 'Walmart', color: '#0071CE' },
                      { id: 'kroger', name: 'Kroger', color: '#0066B2' },
                    ].map((store) => (
                      <div 
                        key={store.id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                        data-testid={`store-${store.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-6 h-6 rounded"
                            style={{ backgroundColor: store.color }}
                          />
                          <span className="font-medium">{store.name}</span>
                        </div>
                        <Switch
                          checked={config?.stores?.enabled_stores?.includes(store.id) ?? true}
                          onCheckedChange={(checked) => {
                            const current = config?.stores?.enabled_stores || ['instacart', 'amazon', 'walmart'];
                            const updated = checked 
                              ? [...current, store.id]
                              : current.filter(s => s !== store.id);
                            setConfig(prev => ({
                              ...prev,
                              stores: { ...prev.stores, enabled_stores: updated }
                            }));
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Default Store</Label>
                  <select 
                    className="w-full p-2 border rounded-md"
                    value={config?.stores?.default_store || 'instacart'}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      stores: { ...prev.stores, default_store: e.target.value }
                    }))}
                    data-testid="default-store-select"
                  >
                    <option value="instacart">Instacart</option>
                    <option value="amazon">Amazon Fresh</option>
                    <option value="walmart">Walmart</option>
                    <option value="kroger">Kroger</option>
                  </select>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Show Store Logos</p>
                    <p className="text-sm text-muted-foreground">Display store brand logos in export UI</p>
                  </div>
                  <Switch
                    checked={config?.stores?.show_store_logos !== false}
                    onCheckedChange={(checked) => setConfig(prev => ({
                      ...prev,
                      stores: { ...prev.stores, show_store_logos: checked }
                    }))}
                    data-testid="show-logos-switch"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Preview Panel */}
        {previewMode && (
          <Card className="mt-6 border-primary">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Live Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="p-6 rounded-lg"
                style={{ 
                  backgroundColor: `hsl(${config?.brand?.colors?.background || '40 33% 98%'})`,
                  color: `hsl(${config?.brand?.colors?.foreground || '60 4% 11%'})`
                }}
              >
                <h2 
                  className="text-2xl font-bold mb-2"
                  style={{ color: `hsl(${config?.brand?.colors?.primary || '125 16% 36%'})` }}
                >
                  {config?.brand?.name || 'Carted'}
                </h2>
                <p className="text-sm opacity-70">{config?.brand?.tagline}</p>
                <Button 
                  className="mt-4"
                  style={{ 
                    backgroundColor: `hsl(${config?.brand?.colors?.primary || '125 16% 36%'})`,
                    color: `hsl(${config?.brand?.colors?.primary_foreground || '0 0% 100%'})`
                  }}
                >
                  {config?.copy?.export_to_store || 'Send to Store'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>White-label configurations are applied in real-time.</p>
          <p>Changes affect all users of the specified tenant.</p>
        </div>
      </div>
    </div>
  );
};

export default WhiteLabelAdmin;
